import { useRouter } from "next/router";
import { useState } from "react";
import CustomModal from "~components/CustomModal/CustomModal";
import { IReleaseTicket } from "~features/nonseated-reservation/nonseated-reservation.types";
import { CreateOrderResultOrder } from "~hooks/reservation/types";
import { formatTicketName } from "~lib";
import {
  HighDemandModalContent,
  HighDemandModalContentAffectedTicket,
} from "./HighDemandModalContent";
import { LockingInPriceSpinner } from "./LockingInPriceSpinner";

interface LockingInPriceFlowModalProps
  extends LockingInPriceFlowModalContentsProps {
  isOpen: boolean;
  onClose: () => void;
}

export function LockingInPriceFlowModal({
  isOpen,
  onClose,
  ...contentsProps
}: LockingInPriceFlowModalProps) {
  return (
    <CustomModal isOpen={isOpen} close={onClose}>
      <LockingInPriceFlowModalContents {...contentsProps} />
    </CustomModal>
  );
}

interface LockingInPriceFlowModalContentsProps {
  selectedTickets: IReleaseTicket[];
  order?: CreateOrderResultOrder;
  showFees: boolean;
  onPriceFlowComplete: (order: CreateOrderResultOrder) => void;
}

export function LockingInPriceFlowModalContents({
  selectedTickets,
  order,
  showFees,
  onPriceFlowComplete,
}: LockingInPriceFlowModalContentsProps) {
  const [highDemandAffectedTickets, setHighDemandAffectedTickets] = useState<
    HighDemandModalContentAffectedTicket[]
  >([]);

  if (highDemandAffectedTickets.length > 0) {
    return (
      <HighDemandModalContent
        affectedTickets={highDemandAffectedTickets}
        order={order}
        showFees={showFees}
        onComplete={() => onPriceFlowComplete(order)}
      />
    );
  }

  return (
    <LockingInPriceSpinner
      isLoading={!order}
      onAnimationComplete={() => {
        const affectedTickets = findHighDemandAffectedTickets(
          selectedTickets,
          order.lineItems
        );

        if (affectedTickets.length > 0) {
          return setHighDemandAffectedTickets(affectedTickets);
        }

        onPriceFlowComplete(order);
      }}
    />
  );
}

/**
 * Finds details for tickets that have a different pricing tier and price in
 * the order than those shown to the user on the reservation page.
 *
 * NOTE: This function makes the following presumptions:
 * - All selected tickets of the same type have the same pricing tier.
 * - All tickets of the same type will share a single line item, (and will
 *   therefore have the same pricing tier).
 */
function findHighDemandAffectedTickets(
  selectedTickets: IReleaseTicket[],
  orderLineItems: CreateOrderResultOrder["lineItems"]
): HighDemandModalContentAffectedTicket[] {
  const selectedTicketsWithPricingTiers: Record<string, IReleaseTicket> = {};
  for (const ticket of selectedTickets) {
    if (ticket.pricingTierId) {
      selectedTicketsWithPricingTiers[ticket.id] = ticket;
    }
  }

  const highDemandAffectedTickets: HighDemandModalContentAffectedTicket[] = [];
  for (const { node: lineItem } of orderLineItems.edges) {
    const ticketType = lineItem.ticketType;

    if (!ticketType) {
      continue;
    }

    const selectedTicket = selectedTicketsWithPricingTiers[ticketType.id];

    // NOTE: We fall back to `undefined` as these could be `null`, from the API.
    const expectedPricingTierId = selectedTicket?.pricingTierId ?? undefined;
    const actualPricingTierId = lineItem.pricingTierId ?? undefined;

    if (actualPricingTierId === expectedPricingTierId) {
      continue;
    }

    const expectedPrice = selectedTicket?.price ?? 0;
    const actualPrice = lineItem.price;
    const priceChange = actualPrice - expectedPrice;

    // We ignore tier differences when the price hasn't changed, to minimise
    // checkout friction for the user.
    if (priceChange === 0) {
      continue;
    }

    highDemandAffectedTickets.push({
      lineItemId: lineItem.id,
      name: formatTicketName(ticketType.name, lineItem.pricingTierName),
      price: lineItem.price,
      fee: lineItem.ticketFeeAmount,
      quantity: lineItem.quantity,
      priceChange,
      // Only consider `inHighDemand` because the ticket must be available as
      // it's in the user's cart.
      customBadge: selectedTicket?.inHighDemand
        ? {
            label: selectedTicket.customBadge,
            textColor: selectedTicket.customBadgeTextColor,
            backgroundColor: selectedTicket.customBadgeBackgroundColor,
          }
        : undefined,
    });
  }

  return highDemandAffectedTickets;
}
